import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  StyleSheet,
  View,
  type LayoutChangeEvent,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { durations } from '../../theme';
import { useReducedMotion } from '../../hooks/useReducedMotion';

interface AnimatedHeightProps {
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}

/**
 * A container whose height eases to fit its content when the content
 * changes size, instead of jumping. The content swaps at once (nothing of
 * the old state lingers, so tests and focus see the new tree immediately);
 * only the box around it grows or shrinks, over `durations.base`.
 *
 * How: the content sits absolutely inside an outer box whose height this
 * component owns. The content reports its height with onLayout, and the box
 * animates to it. Before the first measurement the content is in normal
 * flow, so the first paint is the real size (no animation on mount, and
 * environments that never fire onLayout, like jest, render normally).
 * `overflow: hidden` applies only while the box is moving, so focus rings
 * and shadows at the edges are not clipped the rest of the time.
 *
 * Reduced motion sets the height at once.
 */
export function AnimatedHeight({ children, style }: AnimatedHeightProps) {
  const reducedMotion = useReducedMotion();
  const height = useRef(new Animated.Value(0)).current;
  const target = useRef<number | null>(null);
  const [measured, setMeasured] = useState(false);
  const [moving, setMoving] = useState(false);
  const animation = useRef<Animated.CompositeAnimation | null>(null);

  useEffect(() => () => animation.current?.stop(), []);

  const onContentLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const next = Math.round(e.nativeEvent.layout.height * 100) / 100;
      if (target.current === null) {
        target.current = next;
        height.setValue(next);
        setMeasured(true);
        return;
      }
      if (next === target.current) return;
      target.current = next;
      animation.current?.stop();
      if (reducedMotion) {
        height.setValue(next);
        setMoving(false);
        return;
      }
      setMoving(true);
      const run = Animated.timing(height, {
        toValue: next,
        duration: durations.base,
        easing: Easing.out(Easing.cubic),
        // Height is a layout property; the native driver can't animate it.
        useNativeDriver: false,
      });
      animation.current = run;
      run.start(({ finished }) => {
        if (finished) setMoving(false);
      });
    },
    [height, reducedMotion]
  );

  return (
    <Animated.View style={[style, measured && { height }, measured && moving && styles.clip]}>
      <View
        onLayout={onContentLayout}
        style={measured ? styles.contentOverlay : undefined}
        collapsable={false}>
        {children}
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  clip: { overflow: 'hidden' },
  contentOverlay: { position: 'absolute', top: 0, left: 0, right: 0 },
});
